import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { CheckCircle2, Copy, ExternalLink, Gift, Loader2, Smartphone, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ScratchCard from './ScratchCard';
import ConfettiBurst from './ConfettiBurst';
import { getBookingPartnerReward, revealBookingPartnerReward } from '@/api/partnerRewards';
import { track } from '@/lib/analytics';
import kuickoMark from '@/assets/images/kuicko/kuicko-mark.png';
import kuickoWordmark from '@/assets/images/kuicko/kuicko-wordmark.png';

/**
 * "You've earned a Kuicko reward" — the LK → Kuicko cross-app voucher for a
 * booking, as a scratch card on the confirmation page.
 *
 * Everything shown comes from the backend. `state: 'none'` (no campaign
 * matched, feature off, older backend, any error) renders nothing, so a
 * booking without a reward never sees an empty promise.
 */
const POLL_MS = 3000;
const MAX_POLLS = 20; // ~60s; delivery to Kuicko normally lands in a second or two

const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

const formatExpiry = (iso) => {
  if (!iso) return null;
  const d = parseISO(String(iso));
  return isValid(d) ? format(d, 'd MMM yyyy') : null;
};

const valueBadge = (r) => {
  const v = Number(r?.value);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (r.discountType === 'PERCENT') return `${v}% OFF`;
  if (r.discountType === 'AMOUNT') return `₹${v} OFF`;
  return null;
};

const KuickoRewardCard = ({ bookingId, className = 'mt-6' }) => {
  const [reward, setReward] = useState(null);
  const [polls, setPolls] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [revealing, setRevealing] = useState(false);
  // Whether the foil is off. Seeded from the server (revisits open already
  // scratched) but otherwise only flips when the user finishes scratching —
  // the reveal request returning mid-scratch must not yank the foil away.
  const [uncovered, setUncovered] = useState(null);
  const revealPromise = useRef(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Initial load + polling while the grant is on its way to Kuicko.
  useEffect(() => {
    if (!bookingId) return undefined;
    let timer;
    const load = async () => {
      try {
        const data = await getBookingPartnerReward(bookingId);
        if (!alive.current) return;
        setReward((prev) => (prev?.code && !data.code ? { ...data, code: prev.code } : data));
        setUncovered((u) => (u === null ? !!data.revealed : u));
        if (data.state === 'pending') {
          setPolls((n) => {
            if (n + 1 < MAX_POLLS) timer = setTimeout(load, POLL_MS);
            return n + 1;
          });
        }
      } catch {
        if (alive.current) setReward({ state: 'none' });
      }
    };
    load();
    return () => clearTimeout(timer);
  }, [bookingId]);

  useEffect(() => {
    if (reward?.state === 'ready' || reward?.state === 'pending') {
      track('partner_reward_seen', { bookingId, state: reward.state });
    }
    // Only when the state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reward?.state]);

  // Kick the reveal request off on the first scratch, so the code is usually
  // back before the foil is gone.
  const startReveal = useCallback(() => {
    if (!revealPromise.current) {
      revealPromise.current = revealBookingPartnerReward(bookingId)
        .then((data) => {
          if (alive.current && data) setReward(data);
          return data;
        })
        .catch(() => {
          revealPromise.current = null;
          return null;
        });
    }
    return revealPromise.current;
  }, [bookingId]);

  const onRevealed = useCallback(async () => {
    setUncovered(true);
    setCelebrate(true);
    track('partner_reward_revealed', { bookingId });
    const data = await startReveal();
    if (!data?.code) toast.error("Couldn't load your code — try refreshing the page");
  }, [bookingId, startReveal]);

  const revealByButton = async () => {
    setRevealing(true);
    await onRevealed();
    if (alive.current) setRevealing(false);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(reward.code);
      toast.success('Code copied — paste it at Kuicko checkout');
      track('partner_reward_code_copied', { bookingId });
    } catch {
      toast.error('Could not copy the code');
    }
  };

  if (!reward || reward.state === 'none') return null;

  const expiry = formatExpiry(reward.expiresAt);
  const badge = valueBadge(reward);
  const android = isAndroid() && reward.links?.android;
  const primaryLink = android ? reward.links.android : reward.links?.web;

  // ── Pending: the grant exists but Kuicko hasn't answered yet ────────────
  if (reward.state === 'pending') {
    const gaveUp = polls >= MAX_POLLS;
    return (
      <section aria-live="polite" className={`lk-card overflow-hidden p-5 sm:p-6 ${className}`}>
        <Header />
        <div className="mt-4 flex items-center gap-4 rounded-2xl bg-surface-sunken p-4">
          {gaveUp ? (
            <Gift className="h-8 w-8 shrink-0 text-[#FE5F1E]" aria-hidden="true" />
          ) : (
            <Loader2 className="h-8 w-8 shrink-0 animate-spin text-[#FE5F1E]" aria-hidden="true" />
          )}
          <div>
            <p className="font-semibold text-ink-900">
              {gaveUp ? 'Your reward is on its way' : 'Preparing your reward…'}
            </p>
            <p className="mt-0.5 text-sm text-ink-500">
              {gaveUp
                ? "It'll show up in your Kuicko coupons shortly, and here in My Bookings."
                : 'This only takes a moment.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  // ── Used / expired: compact, informational ─────────────────────────────
  if (reward.state === 'redeemed' || reward.state === 'expired') {
    const used = reward.state === 'redeemed';
    return (
      <section className={`lk-card flex items-center gap-4 p-5 ${className}`}>
        <img src={kuickoMark} alt="" className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-ink-900">{reward.title}</p>
          <p className={`mt-0.5 text-sm ${used ? 'text-success' : 'text-ink-500'}`}>
            {used ? 'Used on Kuicko' : `Expired${expiry ? ` on ${expiry}` : ''}`}
          </p>
        </div>
        {used && <CheckCircle2 className="h-6 w-6 shrink-0 text-success" aria-hidden="true" />}
      </section>
    );
  }

  // ── Ready: the scratch card ────────────────────────────────────────────
  const revealed = !!uncovered;

  return (
    <section className={`lk-card relative overflow-visible p-5 sm:p-6 ${className}`} aria-labelledby="kuicko-reward-title">
      <Header />

      <h2 id="kuicko-reward-title" className="mt-3 font-display text-h3 text-ink-900">
        {revealed ? 'Your Kuicko reward' : "You've earned a Kuicko reward"}
      </h2>
      <p className="mt-1 text-sm text-ink-500">
        {revealed
          ? 'Use it on your next Kuicko order.'
          : 'A thank-you for booking with LK. Scratch the card to reveal your code.'}
      </p>

      <div className="relative mt-4">
        {celebrate && <ConfettiBurst />}
        <ScratchCard
          revealed={revealed}
          onScratchStart={startReveal}
          onReveal={onRevealed}
          label="Scratch to reveal"
          className="shadow-card ring-1 ring-black/5"
        >
          {/* The prize, underneath the foil */}
          <div className="relative flex min-h-[176px] flex-col justify-between gap-4 bg-gradient-to-br from-[#FFF4EE] via-white to-[#F6EEFF] p-5 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <img src={kuickoMark} alt="Kuicko" className="h-14 w-14 shrink-0 rounded-2xl shadow-card" />
              <div className="min-w-0">
                {badge && (
                  <span className="inline-block rounded-full bg-[#FE5F1E] px-2.5 py-0.5 text-xs font-bold tracking-wide text-white">
                    {badge}
                  </span>
                )}
                <p className="mt-1 font-display text-lg font-bold leading-snug text-ink-900">{reward.title}</p>
                {reward.description && (
                  <p className="mt-0.5 line-clamp-2 text-sm text-ink-500">{reward.description}</p>
                )}
              </div>
            </div>

            <div className="shrink-0 text-center sm:text-right">
              <p className="lk-label">Your code</p>
              {reward.code ? (
                <p className="tabular mt-1 rounded-xl border-2 border-dashed border-[#FE5F1E]/60 bg-white px-4 py-2 font-mono text-xl font-bold tracking-[0.18em] text-ink-900">
                  {reward.code}
                </p>
              ) : (
                <span className="mt-1 inline-block h-11 w-40 animate-pulseSoft rounded-xl bg-surface-sunken" />
              )}
            </div>
          </div>
        </ScratchCard>
      </div>

      {!revealed && (
        <div className="mt-3 text-center">
          <button
            type="button"
            onClick={revealByButton}
            disabled={revealing}
            className="lk-focus inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-brand-secondary hover:bg-brand-tint disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            {revealing ? 'Revealing…' : 'Reveal without scratching'}
          </button>
        </div>
      )}

      {revealed && reward.code && (
        <div className="mt-5 animate-floatUp">
          <div className="flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={copyCode} className="lk-btn-ghost flex-1">
              <Copy className="h-4 w-4" aria-hidden="true" />
              Copy code
            </button>
            {primaryLink && (
              <a
                href={primaryLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('partner_reward_open_kuicko', { bookingId, android: !!android })}
                className="lk-btn flex-1 bg-[#FE5F1E] text-white shadow-card hover:bg-[#e5541a]"
              >
                {android ? <Smartphone className="h-4 w-4" aria-hidden="true" /> : <ExternalLink className="h-4 w-4" aria-hidden="true" />}
                {android ? 'Get the Kuicko app' : 'Order on Kuicko'}
              </a>
            )}
          </div>

          <ul className="mt-4 space-y-1.5 text-xs text-ink-500">
            <li className="flex gap-2">
              <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
              Already saved to your Kuicko coupons — just log in to Kuicko with the same mobile number.
            </li>
            <li className="flex gap-2">
              <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
              <span>
                One use
                {reward.minOrder ? ` · on orders above ₹${Number(reward.minOrder)}` : ''}
                {expiry ? ` · valid till ${expiry}` : ''}
              </span>
            </li>
          </ul>
        </div>
      )}
    </section>
  );
};

const Header = () => (
  <div className="flex items-center justify-between gap-3">
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF0E8] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#D9480F]">
      <Gift className="h-3.5 w-3.5" aria-hidden="true" />
      Bonus unlocked
    </span>
    <span className="flex items-center gap-2 text-xs font-semibold text-ink-400">
      LK <span aria-hidden="true">×</span>
      <img src={kuickoWordmark} alt="Kuicko" className="h-4 w-auto" />
    </span>
  </div>
);

export default KuickoRewardCard;
